import { useEffect, useRef, useState } from "react";
import { C } from "../../shared/theme";
import { BottomSheet } from "../../shared/BottomSheet";
import { nudgeParts } from "./nudgeLinks";

// A nudge's words, with every task it names as a link that opens that task (nudgeLinks.js).
export function renderNudgeMessage(message, taskId, tasks, onOpenTask) {
  const parts = onOpenTask ? nudgeParts(message, tasks, taskId) : [{ text: message }];
  return (
    <span>
      {/* A real link, so a long name wraps with the sentence like any other words (a button can't). The link styling
          marks the task, so its quotes are dropped. */}
      {parts.map((part, i) => (part.task ? (
        <a key={i} href={`#task-${part.task.id}`} onClick={e => { e.preventDefault(); e.stopPropagation(); onOpenTask(part.task.id); }}
          style={{ color: C.textGreen, fontWeight: 600, textDecoration: "underline" }}>
          {part.task.text}
        </a>
      ) : part.text))}
    </span>
  );
}

// The nudges before the one showing now, newest first, up to 20: the live nudge is left out, so "Older nudge" never
// shows the same words again.
const earlierNudges = (list, liveId) => (list || []).filter(n => n.id !== liveId).slice(0, 20);

// One of the Nudge card's small round buttons: a 44px tap area around a 30px face, named for what it does.
// `pressed` is for a button that stays on (pin); `title` is the tooltip when it should say more than the name.
function CardIconButton({ label, title = label, pressed, onClick, children }) {
  return (
    <button onClick={onClick} aria-label={label} title={title} aria-pressed={pressed}
      style={{ width: 44, height: 44, flexShrink: 0, display: "grid", placeItems: "center", padding: 0, background: "none", border: "none", cursor: "pointer" }}>
      <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: "50%", display: "grid", placeItems: "center", color: pressed ? C.accent : C.textMid, border: `1.5px solid ${pressed ? C.accent : C.border}`, background: pressed ? C.accentLight : "transparent", boxSizing: "border-box" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill={pressed ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
      </span>
    </button>
  );
}

// The card's footer buttons: one filled (what most people will want next), the rest outlined.
const quietButton = () => ({ minHeight: 44, padding: "0 12px", background: "none", borderRadius: C.rsm, border: `1px solid ${C.border}`, fontSize: 14, color: C.textMid, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" });
const filledButton = () => ({ minHeight: 44, background: C.accent, color: C.onAccent, border: "none", borderRadius: C.rsm, padding: "0 14px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" });

// How long to snooze nudges for, from the Nudge card.
function SnoozeSheet({ options, onPick, onClose }) {
  const firstRef = useRef(null);
  return (
    <BottomSheet labelledBy="snooze-title" startRef={firstRef} onClose={onClose}>
      <h2 id="snooze-title" style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700 }}>Snooze nudges for</h2>
      <div style={{ display: "grid", gap: 8 }}>
        {options.map((opt, i) => (
          <button key={opt.label} ref={i === 0 ? firstRef : undefined} onClick={() => onPick(opt.mins)}
            style={{ minHeight: 48, borderRadius: C.rsm, border: `1px solid ${C.border}`, background: C.surface2, fontSize: 16, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>
            {opt.label}
          </button>
        ))}
        <button onClick={onClose} style={{ minHeight: 44, background: "none", border: "none", fontSize: 15, color: C.textMid, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
      </div>
    </BottomSheet>
  );
}

// Radar's Nudge card: the latest nudge, older ones to browse back through, pin, snooze, Dismiss and Next (Craig, 30 Oct
// 2026: "one for 'Dismiss' (which doesn't surface the next nudge) and one for 'Next' (which dismisses the current nudge
// and presents the next one)"). onNext(id) shows the next nudge due now; when there is none the card stays, with
// `noneLeft`, and says so instead of vanishing, until onCloseNoneLeft. Next shows only on the latest nudge (on an older
// one it would file away a nudge you aren't looking at) and only when `hasNext` says one is due (Craig, 30 Oct 2026:
// "Yes"); otherwise Dismiss is the filled button. Snooze says it asks how long ("Snooze for…"; Craig: "maybe the
// Snooze button needs to say 'Snooze for …'"): both put the nudge away, and Snooze also stops new ones for a while.
export function NudgeCard({ nudge, onSnooze, onGotIt, onNext, hasNext = false, noneLeft = false, onCloseNoneLeft, onPastNudges, onPin, onUnpin, pinnedNudge, pinnedNudgeId, nudgeHistory, tasks, onOpenTask }) {
  const [showSnooze, setShowSnooze] = useState(false);
  // Folded each time Radar opens (Craig, 26 Oct 2026: "always show the Nudge card collapsed. It can be too overwhelming
  // and dominant"); tap Nudge to read it.
  const [collapsed, setCollapsed] = useState(true);
  const [browseIndex, setBrowseIndex] = useState(null); // null = live nudge
  const pinnedNudgeIdRef = useRef(null);

  // When the engine surfaces a new nudge, reset browse to live — unless pinned
  useEffect(() => {
    if (!pinnedNudgeId) setBrowseIndex(null);
  }, [nudge]); // eslint-disable-line react-hooks/exhaustive-deps

  // When nudgeHistory loads and there's a pinned nudge to restore, jump to its position
  useEffect(() => {
    if (!pinnedNudgeId || pinnedNudgeIdRef.current === pinnedNudgeId) return;
    const history = earlierNudges(nudgeHistory, nudge?.id);
    const idx = history.findIndex(n => n.id === pinnedNudgeId);
    if (idx !== -1) {
      pinnedNudgeIdRef.current = pinnedNudgeId;
      setBrowseIndex(idx);
    }
  }, [pinnedNudgeId, nudgeHistory, nudge?.id]);

  if (!nudge) return noneLeft ? (
    <div role="status" style={{ background: C.surface, borderRadius: C.r, border: `1.5px solid ${C.border}`, padding: "12px 14px", marginBottom: 10, boxShadow: C.shadow }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: C.textGreen }}>Nudge</h2>
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: C.text }}>You've seen every nudge for now. The next one will show here when Nudge sends it.</p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, gap: 8 }}>
        <button onClick={onPastNudges}
          style={{ minHeight: 44, padding: 0, background: "none", border: "none", fontSize: 14, fontWeight: 600, color: C.textGreen, cursor: "pointer", fontFamily: "inherit" }}>
          All nudges ›
        </button>
        <button onClick={onCloseNoneLeft} style={quietButton()}>
          Close
        </button>
      </div>
    </div>
  ) : null;

  const SNOOZE_OPTIONS = [
    { label: "30 minutes",     mins: 30 },
    { label: "1 hour",         mins: 60 },
    { label: "4 hours",        mins: 240 },
    { label: "Until tomorrow", mins: (() => {
      const now = new Date();
      const tom = new Date(now); tom.setDate(tom.getDate() + 1); tom.setHours(6,0,0,0);
      return Math.round((tom - now) / 60000);
    })() },
  ];

  const history = earlierNudges(nudgeHistory, nudge.id);
  const isBrowsing = browseIndex !== null;
  const displayedNudge = isBrowsing ? history[browseIndex] : nudge;
  const isPinned = !!pinnedNudge && pinnedNudge.id === displayedNudge?.id;
  const canGoOlder   = history.length > 0 && (browseIndex === null ? true : browseIndex < history.length - 1);
  const canGoNewer   = browseIndex !== null;

  const showNext = !isBrowsing && hasNext;
  const goOlder = () => setBrowseIndex(prev => prev === null ? 0 : prev + 1);
  const goNewer = () => setBrowseIndex(prev => (prev === 0 ? null : prev - 1));
  const togglePin = () => (isPinned ? onUnpin() : onPin(displayedNudge));

  return (
    <div style={{ background: C.surface, borderRadius: C.r, border: `1.5px solid ${isPinned ? C.accent : C.border}`, padding: collapsed ? "10px 14px 8px" : "12px 14px", marginBottom: 10, boxShadow: C.shadow }}>

      {/* Snooze sheet */}
      {showSnooze && <SnoozeSheet options={SNOOZE_OPTIONS} onPick={(mins) => { onSnooze(mins); setShowSnooze(false); }} onClose={() => setShowSnooze(false)} />}

      {/* Header: the heading is the button that folds the card; the icon buttons beside it browse and pin. */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: collapsed ? 0 : 4, gap: 2 }}>
        {/* Green (Craig asked for colour, 23 Oct 2026), in Today's Inspiration's heading type (Craig, 24 Oct 2026: "match
            the Today's Inspiration card (but keep it green)"). */}
        <h2 style={{ margin: 0, flex: 1, minWidth: 0 }}>
          <button onClick={() => setCollapsed(c => !c)} aria-expanded={!collapsed}
            style={{ width: "100%", minHeight: 44, display: "flex", alignItems: "center", gap: 8, padding: 0, background: "none", border: "none", textAlign: "left", cursor: "pointer", fontFamily: "inherit", fontSize: 18, fontWeight: 700, color: C.textGreen }}>
            {/* Always "Nudge", so it never wraps beside the buttons: the pressed pin and the green border say it's pinned,
                and the line under the heading says how far back you are. */}
            Nudge
            <span aria-hidden="true" style={{ fontSize: 12, color: C.textDim }}>{collapsed ? "▼" : "▲"}</span>
          </button>
        </h2>

        {/* Browsing shows only while the card is open: folded, a tap would change a nudge you can't see. */}
        {!collapsed && canGoNewer && <CardIconButton label="Newer nudge" onClick={goNewer}><polyline points="15 18 9 12 15 6"/></CardIconButton>}
        {!collapsed && canGoOlder && <CardIconButton label="Older nudge" onClick={goOlder}><polyline points="9 18 15 12 9 6"/></CardIconButton>}
        <CardIconButton label="Pin this nudge" title={isPinned ? "Unpin this nudge" : "Pin this nudge"} pressed={isPinned} onClick={togglePin}>
          <line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
        </CardIconButton>
      </div>

      {!collapsed && displayedNudge && <>

        {/* Browse position indicator */}
        {isBrowsing && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.textMid, marginBottom: 6 }}>
            <span style={{ flex: 1 }}>{browseIndex + 1} of {history.length} earlier nudges · {displayedNudge.date} {displayedNudge.time}</span>
            {browseIndex > 0 && (
              <button onClick={() => setBrowseIndex(null)}
                style={{ minHeight: 44, padding: "0 4px", background: "none", border: "none", fontSize: 14, fontWeight: 600, color: C.textGreen, cursor: "pointer", fontFamily: "inherit" }}>
                Latest
              </button>
            )}
          </div>
        )}

        {/* Message */}
        <div style={{ fontSize: 15, color: isBrowsing ? C.textMid : C.text, lineHeight: 1.5, fontWeight: 500 }}>
          {renderNudgeMessage(displayedNudge.message, displayedNudge.taskId, tasks, onOpenTask)}
        </div>

        {/* Footer: "All nudges ›" on its own line, then the buttons together on the right, so the card keeps one height
            whether Next shows or not. */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginTop: 6, rowGap: 2 }}>
          <button onClick={onPastNudges}
            style={{ flexBasis: "100%", minHeight: 44, whiteSpace: "nowrap", padding: 0, background: "none", border: "none", textAlign: "left", fontSize: 14, fontWeight: 600, color: C.textGreen, cursor: "pointer", fontFamily: "inherit" }}>
            All nudges ›
          </button>
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <button onClick={() => setShowSnooze(true)} style={quietButton()}>
              Snooze for…
            </button>
            <button onClick={() => { onGotIt(displayedNudge.id); setBrowseIndex(null); }}
              style={showNext ? quietButton() : filledButton()}>
              Dismiss
            </button>
            {showNext && <button onClick={() => onNext(displayedNudge.id)} style={filledButton()}>
              Next
            </button>}
          </div>
        </div>

      </>}
    </div>
  );
}
